"use client";

import React, { useEffect, useState, useRef, useTransition, useMemo } from "react";
import { CircleCheck, CircleAlert, Loader2, Send, RefreshCw, Mail, PhoneCall, Microscope, FileText, ShieldCheck, ArrowRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "@/components/common/Link";
import Reveal from "@/components/common/Reveal";
import { useLanguage } from "@/hooks/useLanguage";
import { pick } from "@/lib/i18n";
import { COMMON } from "@/lib/commonCopy";
declare const wvcClient: any;
const getWvcClient = () => typeof wvcClient !== "undefined" ? wvcClient : (window as any).wvcClient;
interface FormFieldDefinition {
  id: string;
  key: string;
  type: string;
  label: string;
  placeholder?: string;
  help?: string;
  required?: boolean;
  options?: Array<{
    value: string;
    label: string;
    price?: number;
  }>;
  validation?: {
    messages?: {
      required?: string;
      invalid?: string;
    };
  };
}
interface FormDefinition {
  id: number;
  key: string;
  name: string;
  description: string;
  availability?: {
    accepting: boolean;
    state: string;
    message: string;
  };
  fields: FormFieldDefinition[];
  submit?: {
    label: string;
    processing_label?: string;
  };
  spam?: {
    token: string;
    honeypot?: {
      field: string;
    } | null;
    min_seconds?: number;
    challenge?: any;
  };
}
export default function Consultation() {
  const {
    lang
  } = useLanguage();
  const [, startTransition] = useTransition();
  const [formDef, setFormDef] = useState<FormDefinition | null>(null);
  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [loadErrorMsg, setLoadErrorMsg] = useState<string>("");
  const [formData, setFormData] = useState<Record<string, string>>({
    full_name: "",
    company: "",
    email: "",
    phone: "",
    mesh_interest: "",
    project_details: ""
  });
  const [honeypotVal, setHoneypotVal] = useState<string>("");
  const [fetchTimestamp, setFetchTimestamp] = useState<number>(0);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, {
    message: string;
  }>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submittedSuccess, setSubmittedSuccess] = useState<boolean>(false);
  const [confirmationMessage, setConfirmationMessage] = useState<string>("");
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);
  const copy = useMemo(() => ({
    badge: pick(lang, {
      en: "Engineering Support",
      zh: "工程支持",
      es: "Soporte de ingeniería",
      de: "Engineering-Support",
      fr: "Support d'ingénierie",
      pt: "Suporte de engenharia"
    }),
    h2: pick(lang, {
      en: "Technical mesh engineering consultation",
      zh: "技术丝网工程咨询",
      es: "Consulta técnica de ingeniería de mallas",
      de: "Technische Drahtgewebe-Engineering-Beratung",
      fr: "Consultation technique d'ingénierie des toiles",
      pt: "Consulta técnica de engenharia de malhas"
    }),
    lead: pick(lang, {
      en: "Work directly with MutualFilter application engineers to specify exact weave patterns, aperture tolerances, wire diameters, and multilayer sintered structures for optimal filtration performance.",
      zh: "直接对接 MutualFilter 应用工程师，探讨工业编织过滤网、烧结多层结构与冲压滤筒的工艺选型、公差要求与工况匹配方案。",
      es: "Trabaje directamente con los ingenieros de aplicaciones de MutualFilter para especificar tipos de tejido, tolerancias de apertura, diámetros de alambre y estructuras multicapa sinterizadas.",
      de: "Arbeiten Sie direkt mit den Anwendungsingenieuren von MutualFilter zusammen, um Bindungsarten, Toleranzen, Drahtdurchmesser und Sinterlaminate exakt abzustimmen.",
      fr: "Collaborez directement avec les ingénieurs d'application MutualFilter pour définir les tissages, tolérances d'ouverture, diamètres de fil et structures frittées multicouches.",
      pt: "Trabalhe diretamente com os engenheiros de aplicação da MutualFilter para especificar tramas, tolerâncias de abertura, diâmetros de arame e estruturas sinterizadas."
    }),
    step1Title: pick(lang, {
      en: "Material & grade review",
      zh: "材质与牌号确认",
      es: "Revisión de material y grado",
      de: "Werkstoff- & Gütenprüfung",
      fr: "Validation du matériau et de la nuance",
      pt: "Revisão de material e grau"
    }),
    step1Desc: pick(lang, {
      en: "Review stainless steel grade (304, 316L or other) against the operating medium and temperature.",
      zh: "根据运行介质、化学腐蚀环境与工作温度，评估不锈钢牌号（304、316L 或其他合金）的适用性。",
      es: "Evalúe el grado de acero inoxidable (304, 316L u otras aleaciones) según el medio de trabajo y la temperatura.",
      de: "Bewertung der Edelstahlgüte (304, 316L oder andere Legierungen) bezüglich Betriebsmedium und Temperatur.",
      fr: "Évaluation de la nuance d'acier inoxydable (304, 316L ou autres alliages) selon le fluide et la température.",
      pt: "Avaliação do grau de aço inoxidável (304, 316L ou outras ligas) conforme o meio de processo e a temperatura."
    }),
    step2Title: pick(lang, {
      en: "Aperture & flow calculation",
      zh: "孔径与流量计算",
      es: "Cálculo de apertura y flujo",
      de: "Maschenweiten- & Durchflussberechnung",
      fr: "Calcul d'ouverture et de débit",
      pt: "Cálculo de abertura e vazão"
    }),
    step2Desc: pick(lang, {
      en: "Calculate wire diameter, open area, pressure drop and dirt-holding estimate for stable process flow.",
      zh: "计算丝径、开孔率、流体压降（delta-P）及纳污容限，平衡截留效率与流通阻力。",
      es: "Calcule diámetro de alambre, área abierta, pérdida de carga y capacidad de retención para flujo estable.",
      de: "Berechnung von Drahtdurchmesser, freier Siebfläche, Druckverlust und Schmutzaufnahmekapazität für stabilen Durchsatz.",
      fr: "Calcul du diamètre de fil, de la surface ouverte, de la perte de charge et de la rétention pour un débit régulier.",
      pt: "Cálculo de diâmetro de arame, área aberta, perda de carga e capacidade de retenção para vazão estável."
    }),
    step3Title: pick(lang, {
      en: "Specification & samples",
      zh: "规格与打样",
      es: "Especificación y muestras",
      de: "Spezifikation & Muster",
      fr: "Spécification et échantillons",
      pt: "Especificação e amostras"
    }),
    step3Desc: pick(lang, {
      en: "Verify weave pattern and tolerances against ASTM E2016 / ISO 9044 and prepare sample pack fabrication.",
      zh: "对照 ASTM E2016 / ISO 9044 标准验证织法与公差，提供工程样品及试用规格包。",
      es: "Verifique el patrón de tejido y tolerancias según ASTM E2016 / ISO 9044 y prepare la muestra técnica.",
      de: "Verifikation von Bindungsart und Toleranzen nach ASTM E2016 / ISO 9044 sowie Musterbereitstellung.",
      fr: "Validation du type de tissage et des tolérances selon ASTM E2016 / ISO 9044 et fabrication d'échantillons.",
      pt: "Verificação da trama e tolerâncias conforme ASTM E2016 / ISO 9044 e confecção de amostras para teste."
    }),
    contactTitle: pick(lang, {
      en: "Direct Engineering Contact",
      zh: "工程联络通道",
      es: "Contacto directo de ingeniería",
      de: "Direkter Engineering-Kontakt",
      fr: "Contact direct ingénierie",
      pt: "Contato direto de engenharia"
    }),
    cardTitle: pick(lang, {
      en: "Technical Consultation Request",
      zh: "提交工程规格咨询",
      es: "Solicitud de consulta técnica",
      de: "Anfrage für technische Beratung",
      fr: "Demande de consultation technique",
      pt: "Solicitação de consulta técnica"
    }),
    cardDesc: pick(lang, {
      en: "Provide operating parameters for custom weave and tolerance review.",
      zh: "请填写您的具体工况参数，工程团队将对接评估。",
      es: "Indique los parámetros operativos para revisión técnica de mallas y tolerancias.",
      de: "Geben Sie die Betriebsparameter für Gewebe- und Toleranzabstimmung an.",
      fr: "Indiquez les paramètres de fonctionnement pour l'évaluation de la toile et des tolérances.",
      pt: "Informe os parâmetros operacionais para avaliação de malha e tolerâncias."
    }),
    loadingAria: pick(lang, {
      en: "Loading consultation form",
      zh: "正在加载表单",
      es: "Cargando formulario",
      de: "Formular wird geladen",
      fr: "Chargement du formulaire",
      pt: "Carregando formulário"
    }),
    loadErrorTitle: pick(lang, {
      en: "Unable to load consultation form",
      zh: "无法加载咨询表单",
      es: "No se pudo cargar el formulario",
      de: "Beratungsformular konnte nicht geladen werden",
      fr: "Impossible de charger le formulaire",
      pt: "Não foi possível carregar o formulário"
    }),
    loadErrorFallback: pick(lang, {
      en: "Form definition failed to load. Please check your network.",
      zh: "表单定义加载失败，请检查网络连接。",
      es: "Error al cargar la definición del formulario. Verifique su conexión.",
      de: "Formulardefinition konnte nicht geladen werden. Bitte Netzwerk prüfen.",
      fr: "Échec du chargement du formulaire. Veuillez vérifier votre réseau.",
      pt: "Falha ao carregar a definição do formulário. Verifique sua rede."
    }),
    retryButton: pick(lang, {
      en: "Retry loading form",
      zh: "重试加载表单",
      es: "Reintentar carga",
      de: "Formular erneut laden",
      fr: "Réessayer le chargement",
      pt: "Tentar carregar novamente"
    }),
    successTitle: pick(lang, {
      en: "Consultation Request Received",
      zh: "咨询需求已提交",
      es: "Solicitud de consulta recibida",
      de: "Beratungsanfrage erhalten",
      fr: "Demande de consultation reçue",
      pt: "Solicitação de consulta recebida"
    }),
    successMessage: pick(lang, {
      en: "Thank you. Your technical consultation request has been received — our engineering team will follow up.",
      zh: "感谢您的咨询。工程团队已收到您的技术参数，将对工况与规格进行评估。",
      es: "Gracias. Su solicitud de consulta técnica ha sido recibida; nuestro equipo de ingeniería le responderá.",
      de: "Vielen Dank. Ihre technische Beratungsanfrage ist eingegangen – unser Engineering-Team meldet sich zeitnah.",
      fr: "Merci. Votre demande de consultation technique a bien été reçue ; notre équipe d'ingénierie vous contactera.",
      pt: "Obrigado. Sua solicitação de consulta técnica foi recebida — nossa equipe de engenharia entrará em contato."
    }),
    submitAnother: pick(lang, {
      en: "Submit another inquiry",
      zh: "提交另一项咨询需求",
      es: "Enviar otra consulta",
      de: "Weitere Anfrage senden",
      fr: "Envoyer une autre demande",
      pt: "Enviar outra solicitação"
    }),
    submissionErrorTitle: pick(lang, {
      en: "Submission error",
      zh: "提交未完成",
      es: "Error de envío",
      de: "Übermittlungsfehler",
      fr: "Erreur de soumission",
      pt: "Erro de envio"
    }),
    fullNameLabel: pick(lang, {
      en: "Full Name",
      zh: "联系人姓名",
      es: "Nombre completo",
      de: "Vollständiger Name",
      fr: "Nom complet",
      pt: "Nome completo"
    }),
    fullNamePlaceholder: pick(lang, {
      en: "e.g. John Doe",
      zh: "例如：张工 / 李经理",
      es: "p. ej. Carlos Ruiz",
      de: "z. B. Thomas Weber",
      fr: "ex. Jean Dupont",
      pt: "ex. Carlos Silva"
    }),
    fullNameRequired: pick(lang, {
      en: "Full Name is required.",
      zh: "请输入联系人姓名。",
      es: "El nombre completo es obligatorio.",
      de: "Name ist erforderlich.",
      fr: "Le nom complet est requis.",
      pt: "O nome completo é obrigatório."
    }),
    companyLabel: pick(lang, {
      en: "Company / Organization",
      zh: "企业 / 单位名称",
      es: "Empresa / Organización",
      de: "Unternehmen / Organisation",
      fr: "Entreprise / Organisation",
      pt: "Empresa / Organização"
    }),
    companyPlaceholder: pick(lang, {
      en: "e.g. Apex Industrial Solutions",
      zh: "例如：某某环保工程有限公司",
      es: "p. ej. Soluciones Industriales Apex",
      de: "z. B. Industrietechnik GmbH",
      fr: "ex. Solutions Industrielles SAS",
      pt: "ex. Soluções Industriais Ltda."
    }),
    emailLabel: pick(lang, {
      en: "Work Email",
      zh: "工作电子邮箱",
      es: "Correo corporativo",
      de: "Geschäftliche E-Mail",
      fr: "E-mail professionnel",
      pt: "E-mail corporativo"
    }),
    emailRequired: pick(lang, {
      en: "Email address is required.",
      zh: "请输入工作电子邮箱。",
      es: "El correo electrónico es obligatorio.",
      de: "E-Mail-Adresse ist erforderlich.",
      fr: "L'adresse e-mail est requise.",
      pt: "O endereço de e-mail é obrigatório."
    }),
    emailInvalid: pick(lang, {
      en: "Please enter a valid email address.",
      zh: "请输入有效的电子邮箱格式。",
      es: "Introduzca una dirección de correo válida.",
      de: "Bitte eine gültige E-Mail-Adresse eingeben.",
      fr: "Veuillez saisir une adresse e-mail valide.",
      pt: "Insira um endereço de e-mail válido."
    }),
    phoneLabel: pick(lang, {
      en: "Phone Number",
      zh: "联系电话",
      es: "Teléfono",
      de: "Telefonnummer",
      fr: "Numéro de téléphone",
      pt: "Telefone"
    }),
    phonePlaceholder: pick(lang, {
      en: "+1 800 000 0000",
      zh: "+86 180 0000 0000",
      es: "+34 900 000 000",
      de: "+49 30 0000000",
      fr: "+33 1 00 00 00 00",
      pt: "+55 11 0000 0000"
    }),
    categoryLabel: pick(lang, {
      en: "Mesh Product Category",
      zh: "丝网规格类别",
      es: "Categoría de producto de malla",
      de: "Gewebe-Produktkategorie",
      fr: "Catégorie de produit de toile",
      pt: "Categoria de produto de malha"
    }),
    categoryPlaceholder: pick(lang, {
      en: "Select specification category",
      zh: "选择规格类别",
      es: "Seleccione categoría de especificación",
      de: "Spezifikationskategorie auswählen",
      fr: "Sélectionner une catégorie",
      pt: "Selecione a categoria de especificação"
    }),
    projectDetailsLabel: pick(lang, {
      en: "Operating Parameters & Technical Specifications",
      zh: "运行工况与技术参数",
      es: "Parámetros operativos y especificaciones técnicas",
      de: "Betriebsparameter & technische Spezifikationen",
      fr: "Paramètres de fonctionnement et spécifications techniques",
      pt: "Parâmetros operacionais e especificações técnicas"
    }),
    projectDetailsPlaceholder: pick(lang, {
      en: "Detail medium, operating temperature, target micron rating, differential pressure, and alloy or dimensional requirements...",
      zh: "请说明过滤介质、工作温度、截留精度要求、压力差或外形尺寸...",
      es: "Detalle el medio, temperatura operativa, micraje objetivo, presión diferencial y requisitos dimensionales...",
      de: "Beschreiben Sie Medium, Betriebstemperatur, gewünschte Filterfeinheit, Differenzdruck und Maße...",
      fr: "Précisez le fluide, la température de fonctionnement, le seuil de filtration, la pression différentielle et les dimensions...",
      pt: "Detalhe o meio, temperatura operacional, retenção em micras, pressão diferencial e requisitos dimensionais..."
    }),
    projectDetailsRequired: pick(lang, {
      en: "Project details are required.",
      zh: "请填写运行工况或技术要求。",
      es: "Los detalles del proyecto son obligatorios.",
      de: "Projektdetails sind erforderlich.",
      fr: "Les détails du projet sont requis.",
      pt: "Os detalhes do projeto são obrigatórios."
    }),
    submitLabel: pick(lang, {
      en: "Submit Consultation Request",
      zh: "提交技术咨询",
      es: "Enviar solicitud de consulta",
      de: "Beratungsanfrage absenden",
      fr: "Soumettre la demande de consultation",
      pt: "Enviar solicitação de consulta"
    }),
    processingLabel: pick(lang, {
      en: "Submitting to Engineering...",
      zh: "正在提交工程咨询...",
      es: "Enviando a ingeniería...",
      de: "Wird an das Engineering übermittelt...",
      fr: "Envoi à l'ingénierie en cours...",
      pt: "Enviando para a engenharia..."
    }),
    previewUnavailable: pick(lang, {
      en: "Form service unavailable in preview.",
      zh: "预览环境下表单提交服务不可用。",
      es: "Servicio de formulario no disponible en vista previa.",
      de: "Formulardienst in der Vorschau nicht verfügbar.",
      fr: "Service de formulaire indisponible en prévisualisation.",
      pt: "Serviço de formulário indisponível na pré-visualização."
    }),
    tokenExpired: pick(lang, {
      en: "Security token expired. Please try submitting again.",
      zh: "验证令牌已过期，请重新尝试提交。",
      es: "El token de seguridad ha caducado. Inténtelo de nuevo.",
      de: "Sicherheitstoken abgelaufen. Bitte erneut absenden.",
      fr: "Le jeton de sécurité a expiré. Veuillez réessayer.",
      pt: "O token de segurança expirou. Tente enviar novamente."
    }),
    submissionFailed: pick(lang, {
      en: "Submission was not accepted. Please check the details and retry.",
      zh: "表单提交未能成功，请检查信息后重试。",
      es: "El envío no fue aceptado. Compruebe los datos e inténtelo de nuevo.",
      de: "Übermittlung nicht akzeptiert. Bitte Angaben prüfen und erneut versuchen.",
      fr: "La soumission n'a pas été acceptée. Veuillez vérifier les informations.",
      pt: "O envio não foi aceito. Verifique as informações e tente novamente."
    }),
    networkError: pick(lang, {
      en: "A network error occurred. Please try again or contact engineering directly.",
      zh: "网络通信异常，请稍后重试或直接通过邮箱联系工程团队。",
      es: "Ocurrió un error de red. Inténtelo de nuevo o contacte directamente.",
      de: "Netzwerkfehler aufgetreten. Bitte erneut versuchen oder direkt kontaktieren.",
      fr: "Une erreur réseau est survenue. Veuillez réessayer ou contacter directement l'équipe.",
      pt: "Ocorreu um erro de rede. Tente novamente ou entre em contato diretamente."
    })
  }), [lang]);
  const localizedOptions = useMemo(() => [{
    value: "sintered_mesh",
    label: pick(lang, {
      en: "Sintered Multilayer Wire Mesh",
      zh: "多层烧结丝网",
      es: "Malla de alambre sinterizada multicapa",
      de: "Mehrlagiges Sinterdrahtgewebe",
      fr: "Toile métallique frittée multicouche",
      pt: "Malha de arame sinterizada multicamada"
    })
  }, {
    value: "dutch_weave",
    label: pick(lang, {
      en: "Dutch Weave Filter Cloth",
      zh: "密纹编织过滤布",
      es: "Tela filtrante de tejido Dutch",
      de: "Tressengewebe-Filtertuch",
      fr: "Toile filtrante en tissage hollandais",
      pt: "Tecido filtrante em trama holandesa"
    })
  }, {
    value: "square_mesh",
    label: pick(lang, {
      en: "Industrial Square Woven Mesh",
      zh: "工业方孔编织网",
      es: "Malla tejida cuadrada industrial",
      de: "Industrie-Quadratmaschengewebe",
      fr: "Toile tissée carrée industrielle",
      pt: "Malha tecida quadrada industrial"
    })
  }, {
    value: "wedge_wire",
    label: pick(lang, {
      en: "Wedge Wire & Screen Cylinders",
      zh: "约翰逊楔形丝筛网",
      es: "Tamices y cilindros de alambre en cuña",
      de: "Spaltsiebe & Filterzylinder",
      fr: "Fils profilés et cylindres filtrants",
      pt: "Telas e cilindros de arame em cunha"
    })
  }, {
    value: "custom_assemblies",
    label: pick(lang, {
      en: "Stamped Discs & Filter Assemblies",
      zh: "冲压过滤片与滤筒组件",
      es: "Discos estampados y conjuntos de filtración",
      de: "Stanzfilter und Filterbaugruppen",
      fr: "Disques emboutis et assemblages filtrants",
      pt: "Discos estampados e conjuntos de filtração"
    })
  }, {
    value: "other_specialty",
    label: pick(lang, {
      en: "Specialty Woven Mesh",
      zh: "特种编织丝网",
      es: "Malla tejida especial",
      de: "Spezialdrahtgewebe",
      fr: "Toiles tissées spéciales",
      pt: "Malha tecida especial"
    })
  }], [lang]);
  const fetchFormDefinition = React.useCallback(async () => {
    setLoadState("loading");
    setLoadErrorMsg("");
    setFormError(null);
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      const staticDef: FormDefinition = {
        id: 1,
        key: "technical-consultation",
        name: "Technical Consultation",
        description: "Direct engineering consultation for industrial mesh filtration",
        availability: {
          accepting: true,
          state: "open",
          message: ""
        },
        fields: [{
          id: "f_full_name",
          key: "full_name",
          type: "text",
          label: "Full Name",
          placeholder: "e.g. John Doe",
          required: true
        }, {
          id: "f_company",
          key: "company",
          type: "text",
          label: "Company / Organization",
          placeholder: "e.g. Apex Industrial Solutions",
          required: false
        }, {
          id: "f_email",
          key: "email",
          type: "email",
          label: "Work Email",
          placeholder: "name@company.com",
          required: true
        }, {
          id: "f_phone",
          key: "phone",
          type: "phone",
          label: "Phone Number",
          placeholder: "+1 (555) 019-2834",
          required: false
        }, {
          id: "f_mesh_interest",
          key: "mesh_interest",
          type: "select",
          label: "Mesh Product Category",
          placeholder: "Select specification category",
          required: false,
          options: localizedOptions
        }, {
          id: "f_project_details",
          key: "project_details",
          type: "textarea",
          label: "Operating Conditions & Technical Specifications",
          placeholder: "Detail media, temperature, micron rating requirement, differential pressure, and dimensions...",
          required: true
        }],
        submit: {
          label: "Submit Consultation Request",
          processing_label: "Submitting..."
        },
        spam: {
          token: "preview-token",
          honeypot: {
            field: "frms_hp_tech_req"
          },
          min_seconds: 1
        }
      };
      if (isMountedRef.current) {
        setFormDef(staticDef);
        setFetchTimestamp(Date.now());
        setLoadState("ready");
      }
      return;
    }
    try {
      const def = await client.pluginRequest("wvc-forms/v1/forms/technical-consultation");
      if (isMountedRef.current) {
        setFormDef(def);
        setFetchTimestamp(Date.now());
        setLoadState("ready");
      }
    } catch (err: any) {
      if (err?.status === 404) {
        try {
          const listRes = await client.pluginRequest("wvc-forms/v1/forms");
          const matched = listRes?.items?.find((item: any) => item.key === "technical-consultation" || item.name && item.name.toLowerCase().includes("consultation")) || (listRes?.items?.length === 1 ? listRes.items[0] : null);
          if (matched && matched.key) {
            const resolved = await client.pluginRequest(`wvc-forms/v1/forms/${matched.key}`);
            if (isMountedRef.current) {
              setFormDef(resolved);
              setFetchTimestamp(Date.now());
              setLoadState("ready");
              return;
            }
          }
        } catch (_) {}
      }
      if (isMountedRef.current) {
        setLoadErrorMsg(copy.loadErrorFallback);
        setLoadState("error");
      }
    }
  }, [copy.loadErrorFallback, localizedOptions]);
  useEffect(() => {
    fetchFormDefinition();
  }, [fetchFormDefinition]);
  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }));
    const fieldDef = formDef?.fields?.find(f => f.key === key);
    const fieldId = fieldDef?.id || key;
    if (fieldErrors[fieldId] || fieldErrors[key]) {
      setFieldErrors(prev => {
        const next = {
          ...prev
        };
        delete next[fieldId];
        delete next[key];
        return next;
      });
    }
    if (formError) setFormError(null);
  };
  const getFieldDef = (key: string): FormFieldDefinition | undefined => {
    return formDef?.fields?.find(f => f.key === key);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formDef || submitting) return;
    setFormError(null);
    setFieldErrors({});
    const client = getWvcClient();
    if (!client?.pluginRequest) {
      setFormError(copy.previewUnavailable);
      return;
    }
    const fullNameDef = getFieldDef("full_name");
    const emailDef = getFieldDef("email");
    const projectDetailsDef = getFieldDef("project_details");
    const errors: Record<string, {
      message: string;
    }> = {};
    if (!formData.full_name || !formData.full_name.trim()) {
      const id = fullNameDef?.id || "full_name";
      errors[id] = {
        message: fullNameDef?.validation?.messages?.required || copy.fullNameRequired
      };
    }
    if (!formData.email || !formData.email.trim()) {
      const id = emailDef?.id || "email";
      errors[id] = {
        message: emailDef?.validation?.messages?.required || copy.emailRequired
      };
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      const id = emailDef?.id || "email";
      errors[id] = {
        message: emailDef?.validation?.messages?.invalid || copy.emailInvalid
      };
    }
    if (!formData.project_details || !formData.project_details.trim()) {
      const id = projectDetailsDef?.id || "project_details";
      errors[id] = {
        message: projectDetailsDef?.validation?.messages?.required || copy.projectDetailsRequired
      };
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    const minSec = formDef.spam?.min_seconds ?? 3;
    const elapsedSec = (Date.now() - fetchTimestamp) / 1000;
    if (elapsedSec < minSec) {
      await new Promise(resolve => setTimeout(resolve, (minSec - elapsedSec + 0.5) * 1000));
    }
    setSubmitting(true);
    const valuesPayload: Record<string, string> = {};
    formDef.fields.forEach(field => {
      if (formData[field.key] !== undefined && formData[field.key] !== "") {
        valuesPayload[field.id] = formData[field.key];
      }
    });
    const submitPayload: Record<string, any> = {
      values: valuesPayload,
      token: formDef.spam?.token,
      meta: {
        source_url: typeof window !== "undefined" ? window.location.href : "",
        referrer: typeof document !== "undefined" ? document.referrer : ""
      }
    };
    if (formDef.spam?.honeypot?.field) {
      submitPayload.hp = {
        [formDef.spam.honeypot.field]: honeypotVal
      };
    }
    try {
      const res = await client.pluginRequest(`wvc-forms/v1/forms/${formDef.key}/submit`, {
        method: "POST",
        data: submitPayload
      });
      if (res && res.success) {
        if (res.confirmation?.type === "redirect" && res.confirmation.url) {
          window.location.assign(res.confirmation.url);
          return;
        }
        if (res.confirmation?.message) {
          const cleanMsg = res.confirmation.message.replace(/<[^>]*>?/gm, "").trim();
          if (cleanMsg) setConfirmationMessage(cleanMsg);
        }
        startTransition(() => {
          setSubmittedSuccess(true);
          setSubmitting(false);
        });
      } else if (res && res.code === "frms_validation_failed") {
        setFieldErrors(res.errors ?? {});
        setSubmitting(false);
      } else if (res && res.code === "frms_invalid_token") {
        await fetchFormDefinition();
        setFormError(copy.tokenExpired);
        setSubmitting(false);
      } else {
        setFormError(res?.message || copy.submissionFailed);
        setSubmitting(false);
      }
    } catch (err: any) {
      setFormError(err?.message || copy.networkError);
      setSubmitting(false);
    }
  };
  const steps = [{
    icon: Microscope,
    title: copy.step1Title,
    description: copy.step1Desc
  }, {
    icon: FileText,
    title: copy.step2Title,
    description: copy.step2Desc
  }, {
    icon: ShieldCheck,
    title: copy.step3Title,
    description: copy.step3Desc
  }];
  const fullNameDef = getFieldDef("full_name");
  const companyDef = getFieldDef("company");
  const emailDef = getFieldDef("email");
  const phoneDef = getFieldDef("phone");
  const meshInterestDef = getFieldDef("mesh_interest");
  const projectDetailsDef = getFieldDef("project_details");
  const selectOptions = useMemo(() => {
    if (!meshInterestDef?.options || meshInterestDef.options.length === 0) {
      return localizedOptions;
    }
    return meshInterestDef.options.map(opt => {
      const matchingLocal = localizedOptions.find(lo => lo.value === opt.value);
      return {
        value: opt.value,
        label: matchingLocal ? matchingLocal.label : opt.label
      };
    });
  }, [meshInterestDef?.options, localizedOptions]);
  return <section id="consultation" data-nav="light" className="relative mf-section bg-background text-foreground border-t border-border">
      <div className="mf-container">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Technical Context & Process (5 cols) */}
          <Reveal className="lg:col-span-5 space-y-8">
            <div className="space-y-4">
              <Badge variant="outline" className="px-3 py-1 font-mono font-medium tracking-tight mf-caption bg-card border-border text-foreground">
                {copy.badge}
              </Badge>
              <h2 className="mf-h2 text-foreground font-default">
                {copy.h2}
              </h2>
              <p className="text-muted-foreground mf-lead leading-relaxed">
                {copy.lead}
              </p>
            </div>

            {/* 3-Step Process */}
            <div className="space-y-6 pt-2">
              {steps.map((step, i) => {
              const IconComponent = step.icon;
              return <div key={i} data-index={i} className="flex gap-4 items-start">
                    <div className="inline-flex size-10 shrink-0 items-center justify-center rounded-md border border-border bg-muted text-primary">
                      <IconComponent className="size-5" aria-hidden="true" />
                    </div>
                    <div className="space-y-1">
                      <h3 className="mf-h3 text-foreground">
                        {step.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>;
            })}
            </div>

            {/* Direct Technical Contacts & CTA */}
            <div className="p-6 rounded-lg bg-card border border-border space-y-4">
              <div className="text-sm font-semibold text-foreground">
                {copy.contactTitle}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="mailto:sales@mutualfilter.com" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors min-h-[44px]">
                  <Mail className="size-4 text-primary shrink-0" aria-hidden="true" />
                  <span>sales@mutualfilter.com</span>
                </a>
                <a href="tel:+8618033666533" className="inline-flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors min-h-[44px]">
                  <PhoneCall className="size-4 text-primary shrink-0" aria-hidden="true" />
                  <span>+86 180 3366 6533</span>
                </a>
              </div>
              <div className="pt-2 border-t border-border">
                <Button asChild variant="outline" className="w-full justify-between text-sm font-medium min-h-[44px] bg-card">
                  <Link to="/RequestAQuotePage">
                    <span>{pick(lang, COMMON.requestQuote)}</span>
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </Link>
                </Button>
              </div>
            </div>
          </Reveal>

          {/* Right Column: Working Consultation Form (7 cols) */}
          <Reveal delay={120} className="lg:col-span-7">
            <Card className="bg-card text-card-foreground border border-border rounded-lg mf-hover-lift">
              <CardHeader className="border-b border-border bg-muted/20 p-6">
                <CardTitle className="mf-h3 text-foreground font-default">
                  {copy.cardTitle}
                </CardTitle>
                <CardDescription className="text-sm text-muted-foreground">
                  {copy.cardDesc}
                </CardDescription>
              </CardHeader>

              <CardContent className="p-6 md:p-8">
                {/* State: Loading Skeleton */}
                {loadState === "loading" && <div className="space-y-6" aria-busy="true" aria-label={copy.loadingAria}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-11 w-full rounded-md" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-32" />
                        <Skeleton className="h-11 w-full rounded-md" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-11 w-full rounded-md" />
                      </div>
                      <div className="space-y-2">
                        <Skeleton className="h-4 w-28" />
                        <Skeleton className="h-11 w-full rounded-md" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-36" />
                      <Skeleton className="h-11 w-full rounded-md" />
                    </div>
                    <div className="space-y-2">
                      <Skeleton className="h-4 w-44" />
                      <Skeleton className="h-28 w-full rounded-md" />
                    </div>
                    <Skeleton className="h-11 w-44 rounded-md" />
                  </div>}

                {/* State: Load Error with Retry */}
                {loadState === "error" && <div className="py-8 text-center space-y-4">
                    <Alert variant="destructive" className="max-w-xl mx-auto text-left">
                      <CircleAlert className="size-4" aria-hidden="true" />
                      <AlertTitle>{copy.loadErrorTitle}</AlertTitle>
                      <AlertDescription>
                        {loadErrorMsg || copy.loadErrorFallback}
                      </AlertDescription>
                    </Alert>
                    <Button variant="outline" onClick={() => fetchFormDefinition()} className="h-11 gap-2 text-sm min-h-[44px] bg-card">
                      <RefreshCw className="size-4" aria-hidden="true" />
                      {copy.retryButton}
                    </Button>
                  </div>}

                {/* State: Submitted Success */}
                {loadState === "ready" && submittedSuccess && <div className="py-10 text-center max-w-xl mx-auto space-y-5" aria-live="polite">
                    <div className="inline-flex size-12 items-center justify-center rounded-full bg-muted text-primary mb-2 border border-border">
                      <CircleCheck className="size-6" aria-hidden="true" />
                    </div>
                    <h3 className="mf-h3 text-foreground font-default">
                      {copy.successTitle}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">
                      {confirmationMessage || copy.successMessage}
                    </p>
                    <div className="pt-4 flex justify-center">
                      <Button variant="outline" className="text-sm min-h-[44px] bg-card" onClick={() => {
                    setFormData({
                      full_name: "",
                      company: "",
                      email: "",
                      phone: "",
                      mesh_interest: "",
                      project_details: ""
                    });
                    setSubmittedSuccess(false);
                    fetchFormDefinition();
                  }}>
                        {copy.submitAnother}
                      </Button>
                    </div>
                  </div>}

                {/* State: Ready & Interactive Form */}
                {loadState === "ready" && !submittedSuccess && <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                    {/* Honeypot Decoy */}
                    {formDef?.spam?.honeypot?.field && <div style={{
                  position: "absolute",
                  left: "-9999px",
                  opacity: 0
                }} aria-hidden="true">
                        <label htmlFor={formDef.spam.honeypot.field}>Leave empty</label>
                        <input id={formDef.spam.honeypot.field} type="text" name={formDef.spam.honeypot.field} value={honeypotVal} onChange={e => setHoneypotVal(e.target.value)} tabIndex={-1} autoComplete="off" />
                      </div>}

                    {/* Server/General Form Error */}
                    {formError && <div aria-live="polite">
                        <Alert variant="destructive">
                          <CircleAlert className="size-4" aria-hidden="true" />
                          <AlertTitle>{copy.submissionErrorTitle}</AlertTitle>
                          <AlertDescription>{formError}</AlertDescription>
                        </Alert>
                      </div>}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      {/* Full Name */}
                      <div className="space-y-2">
                        <Label htmlFor="consult_full_name" className="text-sm font-medium flex items-center gap-1 text-foreground">
                          <span>{copy.fullNameLabel}</span>
                          <span className="text-destructive">*</span>
                        </Label>
                        <Input id="consult_full_name" type="text" value={formData.full_name} onChange={e => handleInputChange("full_name", e.target.value)} placeholder={copy.fullNamePlaceholder} disabled={submitting} aria-invalid={Boolean(fieldErrors[fullNameDef?.id || "full_name"] || fieldErrors["full_name"])} aria-describedby={fieldErrors[fullNameDef?.id || "full_name"] || fieldErrors["full_name"] ? "err_full_name" : undefined} className="h-11 bg-card text-card-foreground text-sm" />
                        {(fieldErrors[fullNameDef?.id || "full_name"] || fieldErrors["full_name"]) && <p id="err_full_name" className="text-sm text-destructive font-medium">
                            {(fieldErrors[fullNameDef?.id || "full_name"] || fieldErrors["full_name"]).message}
                          </p>}
                      </div>

                      {/* Company */}
                      <div className="space-y-2">
                        <Label htmlFor="consult_company" className="text-sm font-medium text-foreground">
                          {copy.companyLabel}
                        </Label>
                        <Input id="consult_company" type="text" value={formData.company} onChange={e => handleInputChange("company", e.target.value)} placeholder={copy.companyPlaceholder} disabled={submitting} aria-invalid={Boolean(fieldErrors[companyDef?.id || "company"] || fieldErrors["company"])} aria-describedby={fieldErrors[companyDef?.id || "company"] || fieldErrors["company"] ? "err_company" : undefined} className="h-11 bg-card text-card-foreground text-sm" />
                        {(fieldErrors[companyDef?.id || "company"] || fieldErrors["company"]) && <p id="err_company" className="text-sm text-destructive font-medium">
                            {(fieldErrors[companyDef?.id || "company"] || fieldErrors["company"]).message}
                          </p>}
                      </div>

                      {/* Work Email */}
                      <div className="space-y-2">
                        <Label htmlFor="consult_email" className="text-sm font-medium flex items-center gap-1 text-foreground">
                          <span>{copy.emailLabel}</span>
                          <span className="text-destructive">*</span>
                        </Label>
                        <Input id="consult_email" type="email" value={formData.email} onChange={e => handleInputChange("email", e.target.value)} placeholder="name@company.com" disabled={submitting} aria-invalid={Boolean(fieldErrors[emailDef?.id || "email"] || fieldErrors["email"])} aria-describedby={fieldErrors[emailDef?.id || "email"] || fieldErrors["email"] ? "err_email" : undefined} className="h-11 bg-card text-card-foreground text-sm" />
                        {(fieldErrors[emailDef?.id || "email"] || fieldErrors["email"]) && <p id="err_email" className="text-sm text-destructive font-medium">
                            {(fieldErrors[emailDef?.id || "email"] || fieldErrors["email"]).message}
                          </p>}
                      </div>

                      {/* Phone Number */}
                      <div className="space-y-2">
                        <Label htmlFor="consult_phone" className="text-sm font-medium text-foreground">
                          {copy.phoneLabel}
                        </Label>
                        <Input id="consult_phone" type="tel" value={formData.phone} onChange={e => handleInputChange("phone", e.target.value)} placeholder={copy.phonePlaceholder} disabled={submitting} aria-invalid={Boolean(fieldErrors[phoneDef?.id || "phone"] || fieldErrors["phone"])} aria-describedby={fieldErrors[phoneDef?.id || "phone"] || fieldErrors["phone"] ? "err_phone" : undefined} className="h-11 bg-card text-card-foreground text-sm" />
                        {(fieldErrors[phoneDef?.id || "phone"] || fieldErrors["phone"]) && <p id="err_phone" className="text-sm text-destructive font-medium">
                            {(fieldErrors[phoneDef?.id || "phone"] || fieldErrors["phone"]).message}
                          </p>}
                      </div>
                    </div>

                    {/* Mesh Category Select */}
                    <div className="space-y-2">
                      <Label htmlFor="consult_mesh_interest" className="text-sm font-medium text-foreground">
                        {copy.categoryLabel}
                      </Label>
                      <Select value={formData.mesh_interest} onValueChange={val => handleInputChange("mesh_interest", val)} disabled={submitting}>
                        <SelectTrigger id="consult_mesh_interest" className="h-11 w-full bg-card text-card-foreground text-sm" aria-invalid={Boolean(fieldErrors[meshInterestDef?.id || "mesh_interest"] || fieldErrors["mesh_interest"])}>
                          <SelectValue placeholder={copy.categoryPlaceholder} />
                        </SelectTrigger>
                        <SelectContent className="bg-popover text-popover-foreground">
                          {selectOptions.map((opt, i) => <SelectItem key={opt.value} value={opt.value} data-index={i} className="text-sm">
                              {opt.label}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                      {(fieldErrors[meshInterestDef?.id || "mesh_interest"] || fieldErrors["mesh_interest"]) && <p className="text-sm text-destructive font-medium">
                          {(fieldErrors[meshInterestDef?.id || "mesh_interest"] || fieldErrors["mesh_interest"]).message}
                        </p>}
                    </div>

                    {/* Operating Parameters Textarea */}
                    <div className="space-y-2">
                      <Label htmlFor="consult_project_details" className="text-sm font-medium flex items-center gap-1 text-foreground">
                        <span>{copy.projectDetailsLabel}</span>
                        <span className="text-destructive">*</span>
                      </Label>
                      <Textarea id="consult_project_details" rows={4} value={formData.project_details} onChange={e => handleInputChange("project_details", e.target.value)} placeholder={copy.projectDetailsPlaceholder} disabled={submitting} aria-invalid={Boolean(fieldErrors[projectDetailsDef?.id || "project_details"] || fieldErrors["project_details"])} aria-describedby={fieldErrors[projectDetailsDef?.id || "project_details"] || fieldErrors["project_details"] ? "err_project_details" : undefined} className="min-h-28 bg-card text-card-foreground text-sm" />
                      {(fieldErrors[projectDetailsDef?.id || "project_details"] || fieldErrors["project_details"]) && <p id="err_project_details" className="text-sm text-destructive font-medium">
                          {(fieldErrors[projectDetailsDef?.id || "project_details"] || fieldErrors["project_details"]).message}
                        </p>}
                    </div>

                    {/* Action Row */}
                    <div className="pt-2 flex flex-col sm:flex-row items-center justify-end gap-4">
                      <Button type="submit" disabled={submitting} className="w-full sm:w-auto px-8 bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium min-h-[44px]">
                        {submitting ? <>
                            <Loader2 className="size-4 animate-spin mr-2" aria-hidden="true" />
                            {copy.processingLabel}
                          </> : <>
                            <Send className="size-4 mr-2" aria-hidden="true" />
                            {copy.submitLabel}
                          </>}
                      </Button>
                    </div>
                  </form>}
              </CardContent>
            </Card>
          </Reveal>
        </div>
      </div>
    </section>;
}